import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import StatusBadge from "@/components/dashboard/StatusBadge";
import EmptyState from "@/components/dashboard/EmptyState";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function KeywordIntelligence() {
  const { activeProjectId } = useProjects();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [funnel, setFunnel] = useState("all");

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/keywords/${activeProjectId}`);
    setRows(data);
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/keywords/${id}`, { status });
    setRows((r) => r.map((x) => x.id === id ? { ...x, status } : x));
  };

  const filtered = rows
    .filter((r) => !q || r.keyword.toLowerCase().includes(q.toLowerCase()))
    .filter((r) => funnel === "all" || r.funnel_stage === funnel);

  return (
    <DashboardLayout>
      <PageHeader
        overline="Keyword Intelligence"
        title="Long-tail clusters that compound."
        description="Prioritized opportunities mapped to the right page type and the right conversion event for your business model."
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <Input
          data-testid="keyword-search"
          placeholder="Search keywords"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm bg-white border-border rounded-sm"
        />
        <Select value={funnel} onValueChange={setFunnel}>
          <SelectTrigger className="w-44 bg-white rounded-sm" data-testid="funnel-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All funnels</SelectItem>
            <SelectItem value="TOFU">Top of funnel</SelectItem>
            <SelectItem value="MOFU">Mid of funnel</SelectItem>
            <SelectItem value="BOFU">Bottom of funnel</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground self-center">{filtered.length} keywords</span>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={q ? "No matching keywords" : "No keywords tracked yet"}
            description={q ? "Try a different search term, or clear the funnel filter." : "Connect Search Console or run a discovery sweep to start populating keyword opportunities here."}
            testId="keywords-empty-state"
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="keywords-table">
            <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Keyword</th>
                <th className="px-4 py-3">Intent</th>
                <th className="px-4 py-3">Difficulty</th>
                <th className="px-4 py-3">Est. traffic</th>
                <th className="px-4 py-3">Funnel</th>
                <th className="px-4 py-3">Page type</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-ivory-50/50" data-testid={`keyword-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-navy-900">{r.keyword}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.target_url}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{r.intent}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1 bg-ivory-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-gold-500" style={{ width: `${r.difficulty}%` }} />
                      </div>
                      <span className="text-xs">{r.difficulty}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{r.estimated_traffic.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest">{r.funnel_stage}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.recommended_page_type}</td>
                  <td className="px-4 py-3">
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger className="w-36 h-8 rounded-sm bg-white" data-testid={`kw-status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["new", "in_brief", "in_progress", "published", "needs_update"].map((s) => (
                          <SelectItem key={s} value={s}><StatusBadge status={s} /></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}
